import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Rise, Words, EASE } from "./Reveal";
import { Field } from "./Field";
import { COPY } from "../content/copy";
import { api } from "../lib/api";
import { track } from "../lib/track";

const EMPTY = { url: "", what: "", email: "", phone: "", name: "", company: "", blocker: "" };

// form unico (blocco 5, chiusura, /analisi): selettore store/lead + campi
export const LeadForm = ({ position = "middle", extra = false, variant = null, closing = null }) => {
    const f = COPY.form;
    const [mode, setMode] = useState("store");
    const [state, setState] = useState("idle");
    const [v, setV] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const set = (k) => (x) => setV((s) => ({ ...s, [k]: x }));
    const m = f.modes.find((x) => x.id === mode);
    const tid = (s) => (position === "middle" ? s : `${s}-${position}`);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (state === "sending") return;
        setState("sending");
        try {
            let attachment = null;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const up = await fetch(api("/lead/attachment"), { method: "POST", body: fd });
                if (!up.ok) throw new Error("upload");
                attachment = (await up.json()).url;
            }
            const r = await fetch(api("/lead"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    mode, url: m.urlLabel ? v.url : "", what: m.whatLabel ? v.what || null : null,
                    email: v.email, phone: v.phone || null, position,
                    name: v.name || null, company: v.company || null, blocker: v.blocker || null,
                    variant, attachment,
                }),
            });
            if (!r.ok) throw new Error("lead");
            const { download_token } = await r.json();
            track("form_submit", { mode, position, variant });
            setState("done");
            navigate("/grazie", { state: { token: download_token, mode } });
        } catch {
            setState("error");
        }
    };

    return (
        <AnimatePresence mode="wait" initial={false}>
            {state === "done" ? (
                <motion.div key="done" className="confirmation" data-testid={tid("lead-confirmation")}
                    initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
                    <motion.span className="confirmation-line" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.2, ease: EASE }} />
                    <Words text={f.confirmation} delay={0.3} />
                    {closing && <span className="closing">{closing}</span>}
                </motion.div>
            ) : (
                <motion.form key="form" className="lead-form" onSubmit={submit} data-testid={tid("lead-form")} autoComplete="on"
                    exit={{ opacity: 0, y: -18, transition: { duration: 0.5, ease: EASE } }}>
                    <div className="mode-tabs" role="radiogroup" aria-label="Come vendi" data-testid={tid("lead-modes")}>
                        {f.modes.map((o) => (
                            <button key={o.id} type="button" role="radio" aria-checked={mode === o.id}
                                className={`mode-tab ${mode === o.id ? "on" : ""}`} onClick={() => setMode(o.id)}
                                data-testid={tid(`lead-mode-${o.id}`)}>
                                {o.label}
                            </button>
                        ))}
                    </div>
                    {m.urlLabel && (
                        <Field id={tid("lead-url")} name="url" label={m.urlLabel} value={v.url} onChange={set("url")} required testId={tid("lead-input-url")} autoComplete="url" />
                    )}
                    {m.whatLabel && (
                        <Field id={tid("lead-what")} name="what" label={m.whatLabel} value={v.what} onChange={set("what")} required testId={tid("lead-input-what")} />
                    )}
                    <div className="fld-row">
                        <Field id={tid("lead-email")} name="email" label={f.emailLabel} value={v.email} onChange={set("email")} type="email" required testId={tid("lead-input-email")} autoComplete="email" />
                        <Field id={tid("lead-phone")} name="tel" label={f.phoneLabel} value={v.phone} onChange={set("phone")} type="tel" testId={tid("lead-input-phone")} autoComplete="tel" />
                    </div>
                    {extra && (
                        <>
                            <div className="fld-row">
                                <Field id={tid("lead-name")} name="name" label="Nome e cognome (facoltativo)" value={v.name} onChange={set("name")} testId={tid("lead-input-name")} autoComplete="name" />
                                <Field id={tid("lead-company")} name="organization" label="Azienda (facoltativo)" value={v.company} onChange={set("company")} testId={tid("lead-input-company")} autoComplete="organization" />
                            </div>
                            <Field id={tid("lead-blocker")} label="Cosa ti frena oggi? (facoltativo)" value={v.blocker} onChange={set("blocker")} multiline testId={tid("lead-input-blocker")} />
                            <div className="attach" data-testid={tid("lead-attach")}>
                                <label className="attach-label" htmlFor={tid("lead-file")}>
                                    <span className="attach-plus" aria-hidden="true">+</span>
                                    <span className="attach-text">
                                        <span className="attach-title">{file ? file.name : "Allega qualcosa (PDF o immagine, facoltativo)"}</span>
                                        <span className="caption">Brochure, screenshot, listino: tutto quello che ci aiuta a capire.</span>
                                    </span>
                                </label>
                                <input id={tid("lead-file")} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files[0] || null)} data-testid={tid("lead-input-file")} />
                                {file && <button type="button" className="tlink attach-remove" onClick={() => setFile(null)}>Rimuovi</button>}
                            </div>
                        </>
                    )}
                    <div className="lead-actions">
                        <button className="btn btn-solid" type="submit" disabled={state === "sending"} data-testid={tid("lead-submit")}>
                            {state === "sending" ? "Invio…" : f.submit}
                        </button>
                        <p className="caption form-note">{f.note}</p>
                    </div>
                    {state === "error" && <p className="caption form-error" data-testid={tid("lead-error")}>{f.error}</p>}
                </motion.form>
            )}
        </AnimatePresence>
    );
};

export const FormIntro = ({ titleA, titleB, sub, mobileSub, caption, testId = "form" }) => (
    <>
        {caption && <Rise><p className="caption" data-testid={`${testId}-caption`}>{caption}</p></Rise>}
        <Rise delay={0.05}>
            <h2 className="h2 section-title lead-title" data-testid={`${testId}-title`}>
                <Words text={titleA} /> {titleB && <Words text={titleB} italic />}
            </h2>
        </Rise>
        {sub && <Rise delay={0.1} className={mobileSub ? "lead-desktop-copy" : ""}><p className="lead section-lead" data-testid={`${testId}-description`}>{sub}</p></Rise>}
        {mobileSub && <Rise delay={0.1} className="lead-mobile-copy"><p className="lead section-lead" data-testid={`${testId}-mobile-description`}>{mobileSub}</p></Rise>}
    </>
);
